import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Checkbox, Cluster, FormField, Input, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { Flash } from "./Flash";
import { api } from "./api";
import { notifySaved } from "./savedToast";
import { formatAiUsd, money } from "./format";

type FeatureKey = "leadgen" | "loxley" | "mailbox";

interface FeatureBudget {
  key: FeatureKey;
  label: string;
  cap_cents: number | null;
  used_micros: number;
}

export interface OrgAiBudget {
  monthly_budget_cents: number;
  used_cents: number;
  provider: string;
  enabled: boolean;
  remaining_cents: number;
  feature_caps_cents?: Record<FeatureKey, number | null>;
  features?: FeatureBudget[];
}

const FEATURE_NOTES: Record<FeatureKey, string> = {
  leadgen: "Searches, scoring and email drafts.",
  loxley: "One limit for all Loxley seats together, not per seat. MCP calls use the customer's own AI and are not counted.",
  mailbox: "Sorting and summarising shared-mailbox email.",
};

const FEATURE_KEYS: FeatureKey[] = ["leadgen", "loxley", "mailbox"];

/** Dollars typed by staff as cents. Blank means no separate limit; anything unparseable is NaN. */
export function capCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const dollars = Number(trimmed);
  return Number.isFinite(dollars) && dollars >= 0 ? Math.round(dollars * 100) : Number.NaN;
}

function centsInput(cents: number | null | undefined): string {
  return cents == null ? "" : String(cents / 100);
}

/** The organization's monthly AI spend cap, split into optional per-feature limits that fit inside it. */
export function OrgAiBudgetCard({ orgId }: { orgId: string }) {
  const [row, setRow] = useState<OrgAiBudget | null>(null);
  const [budget, setBudget] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [caps, setCaps] = useState<Record<FeatureKey, string>>({ leadgen: "", loxley: "", mailbox: "" });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function apply(next: OrgAiBudget) {
    setRow(next);
    setBudget(centsInput(next.monthly_budget_cents));
    setEnabled(next.enabled);
    setCaps({
      leadgen: centsInput(next.feature_caps_cents?.leadgen),
      loxley: centsInput(next.feature_caps_cents?.loxley),
      mailbox: centsInput(next.feature_caps_cents?.mailbox),
    });
  }

  useEffect(() => {
    api
      .get<OrgAiBudget>(`/operator/orgs/${orgId}/ai`)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "Organization AI could not be loaded."));
  }, [orgId]);

  const budgetCents = capCents(budget);
  const capValues = FEATURE_KEYS.map((key) => capCents(caps[key]));
  const invalid = budgetCents == null || Number.isNaN(budgetCents) || capValues.some((value) => Number.isNaN(value));
  const allocated = capValues.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  const overAllocated = !invalid && allocated > (budgetCents ?? 0);

  async function save() {
    if (invalid || overAllocated || budgetCents == null) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      apply(
        await api.put<OrgAiBudget>(`/operator/orgs/${orgId}/ai`, {
          monthly_budget_cents: budgetCents,
          provider: row?.provider ?? "local",
          enabled,
          feature_caps_cents: { leadgen: capValues[0], loxley: capValues[1], mailbox: capValues[2] },
        }),
      );
      setNotice("Organization AI saved.");
      notifySaved("Organization AI saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Organization AI could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  const used = new Map((row?.features ?? []).map((item) => [item.key, item]));

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="Organization AI"
          tip="Per-tenant AI spend cap and enable flag. Overrides the platform default for this org only. This is what the model calls cost Saleslox, not what the customer pays for the add-ons."
        />
        <p className="admin-muted">
          What this customer&apos;s AI calls may cost Saleslox each month, not what they pay.{" "}
          <Link to="/ai/pricing-guide">How AI pricing works</Link>
        </p>
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        <Cluster gap={2} align="end">
          <FormField
            label="Monthly budget (USD)"
            htmlFor="org-ai-budget"
            tooltip="Hard monthly spend cap for all AI features in this organization together."
          >
            <Input id="org-ai-budget" value={budget} onChange={(event) => setBudget(event.target.value)} />
          </FormField>
          <Checkbox label="Enabled" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          <Button onClick={() => void save()} disabled={saving || !row || invalid || overAllocated}>
            {saving ? "Saving…" : "Save AI"}
          </Button>
        </Cluster>
        {row ? (
          <p className="admin-muted">
            Used {money(row.used_cents, "USD")} of {money(row.monthly_budget_cents, "USD")} this month.
          </p>
        ) : null}
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Monthly limit (USD)</th>
              <th>Used this month</th>
            </tr>
          </thead>
          <tbody>
            {FEATURE_KEYS.map((key, index) => {
              const item = used.get(key);
              const label = item?.label ?? key;
              return (
                <tr key={key}>
                  <td>
                    <div>{label}</div>
                    <div className="admin-muted">{FEATURE_NOTES[key]}</div>
                  </td>
                  <td>
                    <Input
                      aria-label={`${label} monthly limit (USD)`}
                      placeholder="Shared"
                      value={caps[key]}
                      onChange={(event) => setCaps((current) => ({ ...current, [key]: event.target.value }))}
                    />
                  </td>
                  <td>
                    {formatAiUsd((item?.used_micros ?? 0) / 1_000_000)}
                    {capValues[index] != null && !Number.isNaN(capValues[index]) ? ` of ${money(capValues[index] ?? 0, "USD")}` : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {invalid ? (
          <Flash tone="error">Enter amounts in US dollars, for example 4.50. Leave a feature blank to share the budget.</Flash>
        ) : overAllocated ? (
          <Flash tone="error">
            {`Feature limits add up to ${money(allocated, "USD")}, more than the ${money(budgetCents ?? 0, "USD")} monthly budget.`}
          </Flash>
        ) : (
          <p className="admin-muted">
            Allocated {money(allocated, "USD")} of {money(budgetCents ?? 0, "USD")}. Features left blank share the
            remaining {money((budgetCents ?? 0) - allocated, "USD")}, and every feature stops when the monthly budget runs out.
          </p>
        )}
      </Stack>
    </Card>
  );
}
